import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../api/apiClient";
import { useT } from "../i18n/LanguageProvider";

interface Props {
  itemId: string;
  imageKey: string | null;
  alt: string;
  size?: number;
  /** When set, a loaded thumbnail is clickable to open a preview (e.g. lightbox). */
  onThumbnailClick?: (ctx: { itemId: string; imageKey: string }) => void;
  /**
   * G-89: which plane serves the picture. Defaults to the STAFF route.
   *
   * The kiosk must pass its floor pair. `GET /items/:id/image` is behind the
   * CRM's JWT, whose session has an 8-hour server cap the kiosk deliberately
   * does not refresh (OD-35) — so on a tablet left running through a trading
   * day these thumbnails silently stopped loading and every row fell back to
   * the grey placeholder, while the item card (which already used the floor
   * route) still showed the photo. Injected rather than branched on, so there
   * is one thumbnail component and one loading rule.
   *
   * Held in a ref below rather than watched as a dependency: a caller passing
   * an inline object literal would otherwise re-run the effect on every
   * render, releasing and re-acquiring the shared blob on each one.
   */
  imageSource?: {
    /** G-116: `imageKey` versions the address, so a new photo is never served stale. */
    fetchThumbUrl(itemId: string, imageKey: string): Promise<string | null>;
    releaseThumbUrl(itemId: string, imageKey: string): void;
  };
}

const STAFF_IMAGE_SOURCE = {
  fetchThumbUrl: (itemId: string, imageKey: string) =>
    api.fetchItemImageBlobUrl(itemId, { size: "thumb", version: imageKey }),
  releaseThumbUrl: (itemId: string, imageKey: string) =>
    api.releaseItemImageBlobUrl(itemId, { size: "thumb", version: imageKey }),
};

/**
 * Loads catalog item photo via authenticated GET /items/:id/image when imageKey is set.
 * Shares one network request + blob URL per item (thumb) across all rows. Defers fetch
 * until the thumb is near the viewport to limit concurrent loads.
 */
export function ItemThumb({
  itemId,
  imageKey,
  alt,
  size = 40,
  onThumbnailClick,
  imageSource = STAFF_IMAGE_SOURCE,
}: Props) {
  const { t } = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const imageSourceRef = useRef(imageSource);
  imageSourceRef.current = imageSource;

  useEffect(() => {
    if (!imageKey) {
      setShouldLoad(false);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (hit) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [imageKey]);

  useEffect(() => {
    if (!imageKey) {
      setUrl(null);
      setFetchFailed(false);
      return;
    }
    if (!shouldLoad) return;

    let cancelled = false;
    setUrl(null);
    setFetchFailed(false);

    const source = imageSourceRef.current;
    void source.fetchThumbUrl(itemId, imageKey).then((blobUrl) => {
      if (cancelled) return;
      if (!blobUrl) {
        setFetchFailed(true);
        return;
      }
      setUrl(blobUrl);
    });

    return () => {
      cancelled = true;
      source.releaseThumbUrl(itemId, imageKey);
    };
  }, [itemId, imageKey, shouldLoad]);

  const dim = `${size}px`;
  const shell = {
    width: dim,
    height: dim,
    borderRadius: "var(--radius-md)",
    flexShrink: 0,
  } as const;

  let content: ReactNode;
  if (!imageKey || fetchFailed) {
    content = (
      <div
        aria-hidden
        className="inventory-item-thumb inventory-item-thumb--empty"
        style={{
          ...shell,
          background: "var(--color-surface-muted)",
        }}
      />
    );
  } else if (!url) {
    content = (
      <div
        aria-hidden
        className="inventory-item-thumb inventory-item-thumb--loading"
        style={{
          ...shell,
          background: "var(--color-surface-muted)",
        }}
      />
    );
  } else {
    const img = (
      <img
        src={url}
        alt={onThumbnailClick ? "" : alt}
        className="inventory-item-thumb"
        loading="lazy"
        decoding="async"
        style={{
          ...shell,
          objectFit: "cover",
          ...(onThumbnailClick ? { display: "block" } : {}),
        }}
      />
    );
    if (onThumbnailClick && imageKey) {
      const label = alt.trim() || t("thumb.itemPhoto");
      content = (
        <button
          type="button"
          className="inventory-item-thumb-trigger"
          onClick={() => onThumbnailClick({ itemId, imageKey })}
          aria-label={t("thumb.enlarge", { what: label })}
        >
          {img}
        </button>
      );
    } else {
      content = img;
    }
  }

  return (
    <div
      ref={rootRef}
      style={{
        ...shell,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {content}
    </div>
  );
}
